/**
 * @fileoverview aside に表示する用の（週間）ランキングセクションコンポーネント
 */
<script lang="ts">
import { defineComponent, PropType, inject, ref, onMounted, computed } from 'vue'
import { RANKING } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import LayoutRankingContentsCards from '@/components/contents/LayoutRankingContentsCards.vue'
import RankingPeriod from '@/components/date/RankingPeriod.vue'
import ProgressIndicator from '@/components/loading/ProgressIndicator.vue'
import { rankingIndexPath, keywordPath } from '@/generated/railsRoutes'
import { Ref } from 'vue'

export default defineComponent({
  name: 'RankingWeeklyForAside',

  components: {
    LayoutRankingContentsCards,
    RankingPeriod,
    ProgressIndicator,
  },

  props: {
    type: {
      type: String as PropType<'report' | 'keyword'>,
      default: 'report',
    },

    /**
     * 特定のテーマに紐づくランキングを表示する場合のテーマ ID
     * @description 無指定の場合は「すべて」のランキング
     */
    themeId: {
      type: Number,
      default: undefined,
    },

    limit: {
      type: Number,
      default: 5,
    },

    /**
     * ランキングが存在しない場合にランキングセクションを表示するかどうか
     * @description 表示する場合は「現在ランキングはありません」と表示される。
     */
    isShowOnEmpty: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    // constants
    const THEMES = inject<Record<string, string>>('THEMES') || {}

    interface State {
      period: {
        from: string,
        to: string,
      },
      reports: ReportItem[],
      keywords: KeywordItem[],
    }

    const ranking: Ref<State> = ref({
      period: {
        from: '',
        to: '',
      },
      reports: [],
      keywords: [],
    })

    // ランキング取得処理中フラグ
    const isFetching = ref(false)
    const isFetched = ref(false)

    /**
     * props で指定されたテーマ id
     */
    const selectedThemeId = computed<number | undefined>(() => {
      return props.themeId
        ? Number(Object.keys(THEMES).find(key => key === props.themeId?.toString())) || undefined
        : undefined
    })

    /**
     * 見出し部分に表示するテーマ文言
     */
    const headingLabelPrefix = computed<string>(() => {
      return Number(selectedThemeId.value)
        ? THEMES[selectedThemeId.value!.toString()]
        : ''
    })

    /**
     * 「ランキングをもっと見る」の遷移先パス
     */
    const readMoreAnchor = computed<string>(() => {
      return selectedThemeId.value
        ? rankingIndexPath({ theme: selectedThemeId.value })
        : rankingIndexPath()
    })

    const isShowRankingSection = computed<boolean>(() => {
      if (!isFetched.value) return true
      const items = props.type === 'report' ? ranking.value.reports : ranking.value.keywords
      return props.isShowOnEmpty || !!items.length
    })

    onMounted(() => {
      if (props.type === 'report') {
        fetchRanking<WeeklyReportRanking>(
          RANKING.FETCH_WEEKLY_REPORTS,
          {
            theme_id: props.themeId,
            limit: props.limit,
          },
          data => {
            ranking.value.reports = data.reports.map(report => ({
              id: report.id,
              title: report.title,
              thumbImageUrl: report.thumb_image_url,
              to: report.to,
              comment: {
                total: report.total_comments,
              },
              publisher: {
                name: report.publisher.name,
              },
              isRead: report.is_read,
            }))
          },
        )
      } else {
        fetchRanking<WeeklyKeywordRanking>(
          RANKING.FETCH_WEEKLY_KEYWORDS,
          { limit: props.limit },
          data => {
            ranking.value.keywords = data.keywords.map(keyword => ({
              id: keyword.id,
              title: keyword.name,
              to: keywordPath({ id: keyword.id.toString() }),
            }))
          },
        )
      }
    })

    /**
     * ランキングを取得する共通処理
     * @description 取得制御（fetching/fetched）と period のマッピングは共通。
     *              種別ごとに異なる item のマッピングのみ mapItems で受け取る。
     * @param endpoint 取得先 API エンドポイント
     * @param params リクエストパラメータ
     * @param mapItems レスポンスの item を state に詰めるマッピング関数
     */
    const fetchRanking = async <T extends { period: { from: string, to: string } }>(
      endpoint: ApiRequestInfo,
      params: { [key in string]?: number },
      mapItems: (data: T) => void,
    ): Promise<void> => {
      isFetching.value = true
      isFetched.value = false

      try {
        await apiRequestManager.handleRequest({
          endpoint,
          params,
          callback: (response: Response<T>, isSuccess: boolean) => {
            if (!isSuccess) return

            ranking.value.period.from = response.data.period.from
            ranking.value.period.to = response.data.period.to
            mapItems(response.data)
          },
        })
      } catch (error) {
        console.error(error)
      } finally {
        isFetching.value = false
        isFetched.value = true
      }
    }

    return {
      // routes
      rankingIndexPath,

      // data
      ranking,
      isFetching,

      // computed
      headingLabelPrefix,
      readMoreAnchor,
      isShowRankingSection,
    }
  }
})
</script>

<template>
  <section
    v-if="isShowRankingSection"
    class="section">
    <ProgressIndicator v-if="isFetching" />
    <div
      v-else
      class="l-unit__rankingSection">
      <div class="l-unit__rankingSection--header">
        <h2 class="h2 u-text__bold u-text__normal">
          {{ headingLabelPrefix ? `${headingLabelPrefix}の` : '' }}人気{{ type === 'report' ? '記事' : 'キーワード' }}ランキング
        </h2>
      <!-- /.l-unit__rankingSection header --></div>

      <div class="l-unit__rankingSection--description">
        <LayoutRankingContentsCards
          v-if="ranking.reports.length || ranking.keywords.length"
          :items="type === 'report' ? ranking.reports : ranking.keywords"
          category="aside" />

        <p
          v-else
          class="p u-text__center u-text__small">
          現在ランキングはありません
        </p>
      <!-- /.l-unit__rankingSection description --></div>

      <div
        v-if="type === 'report'"
        class="l-unit__rankingSection--action is-readMore">
        <p class="p u-text__right u-text__small">
          <a
            class="a u-action__forward"
            :href="readMoreAnchor">
            ランキングをもっと見る
          </a>
        </p>
      <!-- /.l-unit__rankingSection action --></div>

      <div class="l-unit__rankingSection--footer is-period">
        <RankingPeriod
          heading=""
          :from="ranking.period.from"
          :to="ranking.period.to" />
      <!-- /.l-unit__rankingSection footer --></div>
    <!-- /.l-unit__rankingSection --></div>
  </section>
</template>

<style lang="sass" scoped>
.l-unit__rankingSection
  display: flex
  flex-direction: column

  & &--header
    order: -1

  & &--description
    margin-top: 1.2em

    &:first-child
      margin-top: 0

  & &--action
    &.is-readMore
      margin-top: changeSizePx2Em(20)

  & &--footer
    &.is-period
      order: -1
      margin-top: 0.3em
</style>
